"use client";

import * as React from "react";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Avatar } from "./ui/avatar";
import { AstroMascot } from "./AstroMascot";
import { NotificationBell } from "./NotificationBell";
import { WelcomeBackBanner } from "./WelcomeBackBanner";
import { NotificationProvider, useNotifications } from "./NotificationManager";
import { 
  User, 
  BookOpen, 
  GraduationCap,
  MessageSquare,
  ArrowRight,
  Sparkles,
  Trophy,
  Calendar,
  MapPin,
  Clock,
  Settings,
  LogOut,
  ExternalLink,
  Download,
  Play,
  Users,
  Coffee,
  Star
} from "lucide-react";
const openTurfLogo = '/openturf-logo.svg';

interface WelcomeDashboardProps {
  onShowTerms?: () => void;
  onLogout?: () => void;
  joinerType?: 'intern' | 'consultant' | 'employee';
  userName?: string;
}

function WelcomeDashboardContent({ 
  onShowTerms, 
  onLogout,
  joinerType = 'employee',
  userName = 'Alex Johnson'
}: WelcomeDashboardProps) {
  const { createWelcomeBack } = useNotifications('demo_candidate', 'candidate');
  const [showWelcomeBack, setShowWelcomeBack] = React.useState(false);

  // Check if user is returning (simulate returning user for demo)
  React.useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('dayone_seen_welcome');
    if (!hasSeenWelcome) {
      // First visit
      localStorage.setItem('dayone_seen_welcome', 'true');
    } else {
      // Returning visit - show welcome back banner
      setShowWelcomeBack(true);
      createWelcomeBack(userName);
    }
  }, [userName, createWelcomeBack]);

  // Confetti particles for celebration
  const confettiColors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'];
  
  const getDashboardCards = () => {
    const baseCards = [
      {
        id: 'profile',
        title: 'Profile Management',
        description: 'Complete your profile, update contact details, and manage your personal information.',
        icon: User,
        color: 'from-blue-500 to-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        progress: 85,
        action: 'Complete Profile',
        urgent: false
      },
      {
        id: 'training',
        title: 'Training & Learning',
        description: 'Access your personalized learning path and complete required training modules.',
        icon: GraduationCap,
        color: 'from-purple-500 to-purple-600',
        bgColor: 'bg-purple-50',
        borderColor: 'border-purple-200',
        progress: 0,
        action: 'Start Learning',
        urgent: true,
        badge: '5 modules'
      },
      {
        id: 'handbook',
        title: 'Company Handbook',
        description: 'Everything you need to know about OpenTurf culture, policies, and procedures.',
        icon: BookOpen,
        color: 'from-green-500 to-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        progress: 100,
        action: 'Download PDF',
        urgent: false
      },
      {
        id: 'workspace',
        title: joinerType === 'intern' ? 'Slack Workspace' : 'Team Collaboration',
        description: joinerType === 'intern' 
          ? 'Join your team channels and connect with your mentor and fellow interns.'
          : 'Access Slack, Teams, and other collaboration tools to connect with your colleagues.',
        icon: MessageSquare,
        color: 'from-indigo-500 to-indigo-600',
        bgColor: 'bg-indigo-50',
        borderColor: 'border-indigo-200',
        progress: 0,
        action: 'Join Workspace',
        urgent: true
      }
    ];

    if (joinerType === 'employee') {
      baseCards.push({
        id: 'meetings',
        title: 'Upcoming Meetings',
        description: 'View your calendar and upcoming meetings, including your first team standup.',
        icon: Calendar,
        color: 'from-orange-500 to-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        progress: 100,
        action: 'View Calendar',
        urgent: false,
        badge: '3 this week'
      });
    }

    if (joinerType === 'intern') {
      baseCards.push({
        id: 'mentor',
        title: 'Meet Your Mentor',
        description: 'Connect with your assigned mentor and schedule your first check-in meeting.',
        icon: Users,
        color: 'from-pink-500 to-pink-600',
        bgColor: 'bg-pink-50',
        borderColor: 'border-pink-200',
        progress: 0,
        action: 'Schedule Meeting',
        urgent: true
      });
    }

    return baseCards;
  };

  const dashboardCards = getDashboardCards();

  const getWelcomeMessage = () => {
    const timeOfDay = new Date().getHours() < 12 ? 'morning' : 
                     new Date().getHours() < 18 ? 'afternoon' : 'evening';
    
    switch (joinerType) {
      case 'intern':
        return `Good ${timeOfDay}, ${userName}! Ready to start your internship journey?`;
      case 'consultant':
        return `Good ${timeOfDay}, ${userName}! Let's kick off this exciting project together.`;
      default:
        return `Good ${timeOfDay}, ${userName}! Welcome to your first day at OpenTurf.`;
    }
  };

  const urgentTasks = dashboardCards.filter(card => card.urgent).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-20 left-16 w-32 h-32 bg-blue-200/30 rounded-full blur-3xl"
          animate={{ 
            y: [0, -20, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-20 right-16 w-40 h-40 bg-purple-200/30 rounded-full blur-3xl"
          animate={{ 
            y: [0, 25, 0],
            scale: [1.1, 1, 1.1]
          }}
          transition={{ duration: 10, repeat: Infinity }}
        />
        
        {/* Confetti Animation */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-3 h-3 rounded-full"
            style={{ 
              backgroundColor: confettiColors[i % confettiColors.length],
              left: `${10 + (i * 4)}%`,
              top: `${5 + (i * 2)}%`
            }}
            animate={{
              y: [0, window.innerHeight + 50],
              rotate: [0, 360],
              opacity: [1, 0]
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: i * 0.1,
              ease: "easeOut"
            }}
          />
        ))}
      </div>

      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 p-6 bg-white/70 backdrop-blur-sm border-b border-gray-200/50"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-8 w-auto"
            />
            <div className="h-6 w-px bg-gray-300" />
            <div className="flex items-center space-x-4">
              <Badge className="bg-green-100 text-green-700 border-green-200">
                <Trophy className="w-3 h-3 mr-1" />
                Onboarding Complete!
              </Badge>
              {urgentTasks > 0 && (
                <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                  <Clock className="w-3 h-3 mr-1" />
                  {urgentTasks} urgent task{urgentTasks > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <NotificationBell userId="demo_candidate" userType="candidate" />
            
            <div className="flex items-center space-x-3">
              <Avatar className="w-8 h-8">
                <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-sm">
                  {userName.split(' ').map(n => n[0]).join('')}
                </div>
              </Avatar>
              <div className="hidden md:block">
                <p className="text-sm text-gray-900">{userName}</p>
                <p className="text-xs text-gray-500 capitalize">{joinerType}</p>
              </div>
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              className="text-gray-600 hover:text-gray-800"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </motion.header>

      {/* Welcome Back Banner */}
      {showWelcomeBack && (
        <div className="relative z-10 p-6 pb-0">
          <div className="max-w-7xl mx-auto">
            <WelcomeBackBanner
              candidateName={userName}
              currentStep="Welcome Dashboard"
              onContinue={() => setShowWelcomeBack(false)}
              onDismiss={() => setShowWelcomeBack(false)}
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 p-6">
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Welcome Section with Astro */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Card className="bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 border-indigo-200 p-8 relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-4 right-4">
                  <Sparkles className="w-8 h-8 text-indigo-500" />
                </div>
                <div className="absolute bottom-4 left-4">
                  <Star className="w-6 h-6 text-purple-500" />
                </div>
                <div className="absolute top-1/2 right-1/3">
                  <Trophy className="w-7 h-7 text-pink-500" />
                </div>
              </div>

              <div className="flex items-start space-x-8 relative z-10">
                <motion.div
                  animate={{ 
                    y: [0, -10, 0],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ 
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <AstroMascot expression="excited" size="xl" />
                </motion.div>
                
                <div className="flex-1">
                  <div className="bg-white rounded-2xl p-6 border border-indigo-200 relative shadow-sm">
                    {/* Speech bubble pointer */}
                    <div className="absolute left-0 top-8 w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-r-[12px] border-r-white -translate-x-3" />
                    
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 }}
                    >
                      <h2 className="text-gray-900 mb-2">
                        🎉 Day One. Page One. Welcome aboard!
                      </h2>
                      <p className="text-gray-600 leading-relaxed mb-4">
                        {getWelcomeMessage()} I'm absolutely thrilled you're here! 
                        Your onboarding journey is complete, and now the real adventure begins. 
                        Let's explore what's next on your stellar career path!
                      </p>
                      
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2 text-sm">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                          <span className="text-green-700">All systems ready</span>
                        </div>
                        <div className="flex items-center space-x-2 text-sm">
                          <MapPin className="w-4 h-4 text-blue-600" />
                          <span className="text-gray-600">Bangalore Office</span>
                        </div>
                        <div className="flex items-center space-x-2 text-sm">
                          <Clock className="w-4 h-4 text-purple-600" />
                          <span className="text-gray-600">
                            {new Date().toLocaleDateString('en-IN', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Dashboard Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-gray-900">Your Dashboard</h3>
              <Badge variant="outline" className="text-gray-600">
                {dashboardCards.length} resources available
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {dashboardCards.map((card, index) => (
                <motion.div
                  key={card.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="group"
                >
                  <Card className={`${card.bgColor} ${card.borderColor} border-2 p-6 h-full transition-all duration-300 hover:shadow-lg hover:scale-105 relative overflow-hidden`}>
                    {card.urgent && (
                      <div className="absolute top-3 right-3">
                        <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">
                          Urgent
                        </Badge>
                      </div>
                    )}

                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-12 h-12 bg-gradient-to-r ${card.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                        <card.icon className="w-6 h-6 text-white" />
                      </div>
                      {card.badge && (
                        <Badge variant="outline" className="text-xs">
                          {card.badge}
                        </Badge>
                      )}
                    </div>

                    <h4 className="text-gray-900 mb-2">{card.title}</h4>
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      {card.description}
                    </p>

                    {/* Progress Bar */}
                    {card.progress > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-600">Progress</span>
                          <span className="text-xs text-gray-800">{card.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <motion.div 
                            className={`bg-gradient-to-r ${card.color} h-2 rounded-full`}
                            initial={{ width: 0 }}
                            animate={{ width: `${card.progress}%` }}
                            transition={{ delay: 0.8 + index * 0.1, duration: 1 }}
                          />
                        </div>
                      </div>
                    )}

                    <Button 
                      className={`w-full bg-gradient-to-r ${card.color} text-white hover:opacity-90 transition-opacity group`}
                      size="sm"
                    >
                      {card.action}
                      {card.id === 'handbook' ? (
                        <Download className="w-4 h-4 ml-2" />
                      ) : card.id === 'workspace' ? (
                        <ExternalLink className="w-4 h-4 ml-2" />
                      ) : (
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      )}
                    </Button>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-8"
          >
            <Card className="bg-white/90 backdrop-blur-sm border border-gray-200 p-6">
              <h3 className="text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Button variant="outline" className="h-auto p-4 flex-col space-y-2">
                  <Settings className="w-5 h-5 text-gray-600" />
                  <span className="text-xs">Settings</span>
                </Button>
                <Button variant="outline" className="h-auto p-4 flex-col space-y-2">
                  <Coffee className="w-5 h-5 text-gray-600" />
                  <span className="text-xs">Break Room</span>
                </Button>
                <Button variant="outline" className="h-auto p-4 flex-col space-y-2">
                  <Users className="w-5 h-5 text-gray-600" />
                  <span className="text-xs">Team Directory</span>
                </Button>
                <Button variant="outline" className="h-auto p-4 flex-col space-y-2">
                  <Play className="w-5 h-5 text-gray-600" />
                  <span className="text-xs">Video Tour</span>
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <motion.footer 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="relative z-10 p-6 border-t border-gray-200/50 bg-white/70 backdrop-blur-sm"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-indigo-600 hover:text-indigo-700 hover:underline transition-colors"
            >
              Terms &amp; Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}

export function WelcomeDashboard(props: WelcomeDashboardProps) {
  return (
    <NotificationProvider userId="demo_candidate" userType="candidate">
      <WelcomeDashboardContent {...props} />
    </NotificationProvider>
  );
}